import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const FORM_ID = 'about-feedback-form';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db4rqc0op8h4ruqk263g';

const contentScope = [
  { icon: 'ri-film-line', title: '剧情电影库', desc: '聚合剧情、家庭、悬疑、爱情等类型的优质影片资料。' },
  { icon: 'ri-book-open-line', title: '剧情与主创', desc: '每部影片配有完整剧情简介、导演与主演信息，方便快速了解。' },
  { icon: 'ri-image-2-line', title: '剧照与片段', desc: '精选影片剧照，支持点击放大预览，感受影片氛围。' },
  { icon: 'ri-heart-line', title: '本地收藏', desc: '收藏想看的影片，记录保存在本机浏览器，随时找回。' },
];

const contactItems = [
  { icon: 'ri-mail-line', label: '内容反馈邮箱', value: 'feedback@kind-sister-in-law.demo' },
  { icon: 'ri-time-line', label: '服务时间', value: '每日 09:00 - 22:00（演示）' },
  { icon: 'ri-map-pin-line', label: '站点性质', value: '在线影视内容展示（演示站点）' },
];

export default function About() {
  useDocumentMeta({
    title: '关于我们 - 善良的小姨子影视站',
    description:
      '了解善良的小姨子影视站的定位、内容范围与联系方式。本站为影视内容展示演示站点，播放功能为前端效果演示，不提供真实视频流。',
    keywords: '关于我们,善良的小姨子影视站,影视门户,联系方式,免责声明',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setErrorMessage('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-10 md:pt-14 pb-10 bg-background-950">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-200">关于我们</span>
        </nav>
        <span className="text-xs font-display tracking-[0.3em] text-accent-400">ABOUT US</span>
        <h1 className="mt-2 text-2xl md:text-4xl font-heading font-black text-foreground-50 max-w-3xl leading-tight">
          专注好故事的电影展示平台
        </h1>
        <p className="mt-4 text-sm md:text-base text-foreground-300 leading-relaxed max-w-3xl">
          善良的小姨子影视站是一个以电影《善良的小姨子》为核心的内容展示型站点。
          我们希望用清晰的结构、完整的剧情资料与舒适的浏览体验，帮助每一位观众更快地找到想看的影片。
          本站强调“先了解、再观看”，因此每部影片都提供剧情简介、主创信息与精选剧照。
        </p>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-900">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-1 h-6 rounded-full bg-primary-500"></span>
          <h2 className="text-xl md:text-2xl font-heading font-black text-foreground-50">内容范围</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {contentScope.map((item) => (
            <div key={item.title} className="p-5 md:p-6 rounded-lg bg-background-800/50 border border-background-700/70">
              <span className="w-11 h-11 rounded-md bg-primary-500/12 text-primary-400 flex items-center justify-center">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 text-base font-semibold text-foreground-50">{item.title}</h3>
              <p className="mt-2 text-xs md:text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-950">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1 h-6 rounded-full bg-accent-500"></span>
              <h2 className="text-xl md:text-2xl font-heading font-black text-foreground-50">联系我们</h2>
            </div>
            <p className="text-sm text-foreground-300 leading-relaxed">
              对内容、排版或功能有任何建议，欢迎通过右侧表单反馈。我们会在服务时间内查看并处理。
            </p>
            <div className="mt-6 flex flex-col gap-3">
              {contactItems.map((item) => (
                <div key={item.label} className="flex items-center gap-3 p-4 rounded-lg bg-background-900 border border-background-800">
                  <span className="w-10 h-10 rounded-md bg-secondary-500/12 text-secondary-300 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-400">{item.label}</span>
                    <span className="block text-sm text-foreground-100 truncate">{item.value}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-lg bg-background-900 border border-background-800 p-5 md:p-7">
              <h3 className="text-lg font-semibold text-foreground-50">反馈表单</h3>
              <p className="mt-1 text-sm text-foreground-400">填写下方信息提交你的建议，我们会尽快回复。</p>

              <form
                id={FORM_ID}
                data-readdy-form
                action={SUBMIT_ADDR}
                method="post"
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="fb-name" className="block text-sm text-foreground-200 mb-2">
                      你的称呼
                    </label>
                    <input
                      id="fb-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入称呼"
                      className="w-full px-4 py-3 rounded-md bg-background-800 border border-background-700 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="fb-email" className="block text-sm text-foreground-200 mb-2">
                      电子邮箱
                    </label>
                    <input
                      id="fb-email"
                      name="email"
                      type="email"
                      required
                      placeholder="name@example.com"
                      className="w-full px-4 py-3 rounded-md bg-background-800 border border-background-700 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="fb-topic" className="block text-sm text-foreground-200 mb-2">
                    反馈类型
                  </label>
                  <div className="relative">
                    <select
                      id="fb-topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="w-full appearance-none px-4 py-3 pr-10 rounded-md bg-background-800 border border-background-700 text-sm text-foreground-100 outline-none focus:border-primary-500 transition-colors cursor-pointer"
                    >
                      <option className="bg-background-800">内容建议</option>
                      <option className="bg-background-800">影片推荐</option>
                      <option className="bg-background-800">功能问题</option>
                      <option className="bg-background-800">版权与投诉</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="fb-message" className="block text-sm text-foreground-200 mb-2">
                    反馈内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的建议或问题（最多 500 字）"
                    className="w-full px-4 py-3 rounded-md bg-background-800 border border-background-700 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-primary-500 transition-colors resize-none"
                  ></textarea>
                </div>

                <input
                  className="form-field-extra"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? (
                      <>
                        <span className="w-4 h-4 rounded-full border-2 border-background-50/40 border-t-background-50 animate-spin"></span>
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        提交反馈
                      </>
                    )}
                  </button>

                  {status === 'success' && (
                    <span className="inline-flex items-center gap-2 text-sm text-secondary-300">
                      <i className="ri-checkbox-circle-fill"></i>
                      提交成功，感谢你的反馈！
                    </span>
                  )}
                  {status === 'error' && (
                    <span className="inline-flex items-center gap-2 text-sm text-accent-300">
                      <i className="ri-error-warning-fill"></i>
                      {errorMessage}
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-900">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-1 h-6 rounded-full bg-secondary-500"></span>
          <h2 className="text-xl md:text-2xl font-heading font-black text-foreground-50">版权与免责声明</h2>
        </div>
        <div className="rounded-lg bg-background-800/50 border border-background-700/70 p-5 md:p-7 flex flex-col gap-4 text-sm text-foreground-300 leading-relaxed">
          <p>1. 本站为影视内容展示演示站点，页面中的影片资料、图片与文案仅用于界面与交互效果演示。</p>
          <p>2. 本站不存储、不提供、不传播任何真实视频文件，播放区域为纯前端效果演示，不会加载任何视频流。</p>
          <p>3. 收藏功能基于浏览器本地存储，仅在本机有效，清除浏览器数据后收藏记录将被移除。</p>
          <p>4. 若本站展示内容涉及相关权益问题，请通过上方联系方式与我们取得联系，我们会及时处理。</p>
        </div>
      </section>
    </SiteLayout>
  );
}